/**
 * PORT-008 §8.2 / `_INTRO` §4 — retirer « Argalis » de ce que le SYSTÈME lit.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 LE DÉFAUT QUE CE MODULE FERME
 * ═══════════════════════════════════════════════════════════════════════════
 * Un apprenant à qui son organisme dit « ajoute ton espace à ton écran
 * d'accueil » — le geste que `display: standalone` existe pour encourager —
 * obtenait une icône **Argalis**, étiquetée **Argalis**, à demeure sur son
 * téléphone. Et en tapant dessus il atterrissait sur `start_url: "/"`, c'est-à-
 * dire le login du BACK-OFFICE : le vrai logo Argalis et des arguments de vente
 * adressés à un organisme de formation.
 *
 * Quatre fuites, toutes hors de l'arbre React :
 *   · `public/manifest.json` — `name`, `short_name`, `description`, les icônes ;
 *   · `index.html` — `apple-mobile-web-app-title="Argalis"` ;
 *   · `index.html` — `<link rel="apple-touch-icon">` vers l'icône d'Argalis ;
 *   · `index.html` — `theme-color="#016A63"`, le teal d'Argalis.
 *
 * ⚠️ **Pourquoi aucun gardien ne pouvait le voir.** Celui de la marque blanche
 * (`PortalAuthPages.test.tsx`) monte les composants et balaie le DOM **React**.
 * Ni `index.html` ni `manifest.json` n'y sont : ils sont lus par le système
 * AVANT le premier rendu. Un test qui monte un composant ne verra jamais un
 * fichier statique.
 *
 * ⭐ Et le motif est celui qu'on connaît : `PortalBrandTheme` neutralisait
 * **déjà** `document.title` et le favicon. La préoccupation était comprise ;
 * c'est la dimension VOISINE — ce que lit l'installateur d'application — qui
 * est restée ouverte. *Un correctif ferme la dimension qu'il regarde et laisse
 * ouverte celle qui décide pareil.*
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * POURQUOI UN BLOB, ET PAS UNE ROUTE D'API
 * ═══════════════════════════════════════════════════════════════════════════
 * Un manifeste servi par `api.argalis.app` serait d'une AUTRE origine que la
 * page (`{slug}.argalis.app`) : son `start_url` se résoudrait contre l'origine
 * de l'API, hors de la portée du document, et le navigateur refuserait
 * l'installation. Un `blob:` hérite de l'origine du document — c'est la seule
 * forme qui garde `start_url` et `scope` du bon côté sans serveur de pages.
 *
 * ⚠️ Mode de défaillance, choisi : si un navigateur refuse un manifeste `blob:`,
 * il n'y a **pas** de repli vers celui d'Argalis — le lien pointe ailleurs, donc
 * l'installation n'est simplement pas proposée sur `/espace`. C'est exactement
 * le comportement de l'option « pas d'installation sur /espace », jamais une
 * fuite. iOS, lui, ne lit pas le manifeste pour le titre et l'icône : il lit les
 * balises `apple-*`, que ce module réécrit dans le DOM vivant — la couverture y
 * est totale.
 *
 * ⚠️ L'icône vient de `favicon_url`, puis de `logo_url`. Un logo large donne une
 * icône large, ce qui est laid mais JUSTE ; sans aucune image on RETIRE
 * `apple-touch-icon` plutôt que de laisser celle d'Argalis — iOS pose alors une
 * capture de la page, aux couleurs de l'organisme.
 */

import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import type { PortalBranding } from '@/hooks/usePortalMe.types'

/** Le strict nécessaire d'un `t` — celui d'i18next, ou celui d'un test. */
type Traduire = (cle: string, repli?: string, options?: Record<string, unknown>) => string

/** L'espace, jamais la racine : `"/"` est le login du back-office. */
const DEPART = '/espace'

/**
 * `short_name` est ce qui s'affiche SOUS l'icône, sur une largeur d'environ
 * douze caractères selon la plateforme. Tronquer ici plutôt que laisser le
 * système couper au milieu d'un mot.
 */
function nomCourt(nom: string): string {
    const propre = nom.trim()
    return propre.length <= 12 ? propre : propre.slice(0, 11).trimEnd() + '…'
}

/**
 * Le manifeste de l'espace. Exporté pour être vérifié sans `createObjectURL`.
 *
 * ⚠️ UX-14 — le nom de l'application INSTALLÉE suit le dictionnaire, comme
 * l'onglet (`PortalBrandTheme`) : `Espace ${nom}` en dur donnait une icône
 * nommée en français sur un téléphone en anglais — pour toujours, puisqu'un
 * manifeste est lu à l'installation.
 */
export function manifeste(habillage: PortalBranding, t: Traduire): string {
    const nom = habillage.name.trim()
    const icone = habillage.favicon_url || habillage.logo_url
    const monEspace = t('portal.context.kind.mine')
    return JSON.stringify({
        name: nom ? t('portal.brand.documentTitle', 'Espace {{name}}', { name: nom }) : monEspace,
        short_name: nom ? nomCourt(nom) : nomCourt(monEspace),
        start_url: DEPART,
        scope: DEPART,
        display: 'standalone',
        // ⛔ Jamais `#016A63` en repli : ce serait le teal d'Argalis sur la
        // moitié d'un écran en marque blanche. Sans couleur de charte, on n'en
        // déclare aucune et le navigateur garde son propre habillage.
        ...(habillage.primary_color ? { theme_color: habillage.primary_color } : {}),
        background_color: '#ffffff',
        // ⚠️ `sizes: 'any'` : on ne connaît ni les dimensions ni le format réel
        // de l'image d'un organisme. Mentir sur `192x192` ferait rejeter le
        // manifeste par les vérificateurs d'installabilité.
        icons: icone ? [{ src: icone, sizes: 'any' }] : [],
    })
}

/** Pose `content` sur une balise `<meta name=…>`, et rend de quoi la restaurer. */
function poserMeta(nom: string, contenu: string | null): (() => void) | null {
    const balise = document.querySelector<HTMLMetaElement>(`meta[name="${nom}"]`)
    if (!balise) return null
    const avant = balise.getAttribute('content')
    if (contenu === null) balise.removeAttribute('content')
    else balise.setAttribute('content', contenu)
    return () => {
        if (avant === null) balise.removeAttribute('content')
        else balise.setAttribute('content', avant)
    }
}

/**
 * Réécrit, le temps d'une page d'espace, tout ce qu'un installateur lit.
 *
 * ⚠️ Le nettoyage n'est pas optionnel : sans lui, un salarié de l'organisme qui
 * passerait de `/espace` au back-office verrait son onglet garder le manifeste
 * du client — et l'URL de blob fuirait à chaque montage.
 */
export function useManifesteDeMarque(habillage: PortalBranding | null | undefined): void {
    const { t } = useTranslation()
    useEffect(() => {
        if (!habillage) return

        const defaits: Array<() => void> = []

        /**
         * 🔴 **Les balises d'abord, le manifeste ENSUITE — l'ordre est le
         * correctif.**
         *
         * La première écriture de ce module créait l'URL de blob en tête. Sur un
         * environnement sans `URL.createObjectURL`, l'exception remontait avant
         * la moindre balise et **toute** la réécriture d'identité était
         * abandonnée : le nom, l'icône et le teal d'Argalis restaient en place,
         * c'est-à-dire exactement le défaut qu'on croyait fermer. C'est le
         * gardien qui l'a trouvé, dans un jsdom qui n'implémente pas cette API.
         *
         * Les balises `apple-*` et `theme-color` ne dépendent d'AUCUNE API
         * optionnelle : elles doivent donc être posées les premières, et la
         * partie fragile — le manifeste — être tentée après, sous garde.
         */
        const nom = habillage.name.trim()
        for (const defait of [
            // iOS ne lit PAS le manifeste pour ces deux-là : c'est ici, et nulle
            // part ailleurs, que se joue le nom sous l'icône sur un iPhone.
            poserMeta('apple-mobile-web-app-title', nom ? nomCourt(nom) : null),
            poserMeta('theme-color', habillage.primary_color || null),
            poserMeta('description', null),
        ]) {
            if (defait) defaits.push(defait)
        }

        const touch = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]')
        if (touch) {
            const avant = touch.getAttribute('href')
            const icone = habillage.favicon_url || habillage.logo_url
            // Sans image de l'organisme, on RETIRE l'attribut : iOS pose une
            // capture de la page. Laisser celle d'Argalis serait le défaut même
            // qu'on corrige.
            if (icone) touch.setAttribute('href', icone)
            else touch.removeAttribute('href')
            defaits.push(() => {
                if (avant === null) touch.removeAttribute('href')
                else touch.setAttribute('href', avant)
            })
        }

        const lien = document.querySelector<HTMLLinkElement>('link[rel="manifest"]')
        let url: string | null = null
        if (lien && typeof URL.createObjectURL === 'function') {
            const avant = lien.getAttribute('href')
            try {
                url = URL.createObjectURL(
                    new Blob([manifeste(habillage, (c, r, o) => t(c, r ?? '', o))], {
                        type: 'application/manifest+json',
                    }),
                )
            } catch {
                // Rien à rattraper : sans manifeste réécrit, le lien est simplement
                // retiré (ci-dessous) et l'installation n'est pas proposée. ⛔ Ne
                // JAMAIS laisser retomber sur `/manifest.json` : c'est celui
                // d'Argalis.
                url = null
            }
            if (url) lien.setAttribute('href', url)
            else lien.removeAttribute('href')
            defaits.push(() => {
                if (avant === null) lien.removeAttribute('href')
                else lien.setAttribute('href', avant)
            })
        } else if (lien) {
            const avant = lien.getAttribute('href')
            lien.removeAttribute('href')
            defaits.push(() => {
                if (avant !== null) lien.setAttribute('href', avant)
            })
        }

        return () => {
            for (const defait of defaits) defait()
            // Garde SYMÉTRIQUE de celle de la création : un environnement qui
            // n'a pas `createObjectURL` n'a pas `revokeObjectURL` non plus, et
            // une exception dans un nettoyage d'effet React remonte à l'arbre.
            if (url && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url)
        }
    }, [habillage, t])
}
